import type {PropertyVatProfile} from '@vat/engine/types';

/**
 * Reine Helfer für `vatPropertyProfiles/{propertyID}` (Spec 3.3). Ohne Dokument gilt AUTO_FLAECHE;
 * verschachtelte Felder werden ausschließlich per dotted key aktualisiert.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export type InputTaxKey = PropertyVatProfile['inputTaxKey'];

export const DEFAULT_INPUT_TAX_KEY: InputTaxKey = {mode: 'AUTO_FLAECHE', manualShare: null, note: null};

export const buildDefaultPropertyVatProfile = (params: { propertyID: string; customerID: string | null; uid: string; now: Date }): PropertyVatProfile => ({
    id: params.propertyID,
    propertyID: params.propertyID,
    customerID: params.customerID ?? null,
    inputTaxKey: {...DEFAULT_INPUT_TAX_KEY},
    yearOverrides: {},
    vacancyIntent: {},
    dismissedHints: {},
    createdAt: params.now,
    updatedAt: params.now,
    updatedBy: params.uid,
});

export const resolvePropertyVatProfile = (
    doc: PropertyVatProfile | null | undefined,
    fallback: { propertyID: string; customerID: string | null; now?: Date }
): PropertyVatProfile => {
    if (!doc) {
        return buildDefaultPropertyVatProfile({propertyID: fallback.propertyID, customerID: fallback.customerID, uid: '', now: fallback.now ?? new Date(0)});
    }
    return {
        ...doc,
        inputTaxKey: doc.inputTaxKey ?? {...DEFAULT_INPUT_TAX_KEY},
        yearOverrides: doc.yearOverrides ?? {},
        vacancyIntent: doc.vacancyIntent ?? {},
        dismissedHints: doc.dismissedHints ?? {},
    };
};

export const inputTaxKeyForYear = (profile: PropertyVatProfile, year: number): InputTaxKey =>
    profile.yearOverrides?.[String(year)] ?? profile.inputTaxKey ?? {...DEFAULT_INPUT_TAX_KEY};

export const buildInputTaxKeyUpdate = (key: InputTaxKey, year: number | null, uid: string, now: Date): Record<string, unknown> => {
    const normalizedKey: InputTaxKey = {mode: key.mode, manualShare: key.manualShare ?? null, note: key.note ?? null};
    if (year === null) {
        return {
            'inputTaxKey.mode': normalizedKey.mode,
            'inputTaxKey.manualShare': normalizedKey.manualShare,
            'inputTaxKey.note': normalizedKey.note,
            updatedAt: now,
            updatedBy: uid,
        };
    }
    return {[`yearOverrides.${year}`]: normalizedKey, updatedAt: now, updatedBy: uid};
};

export const buildVacancyIntentUpdate = (unitID: string, intent: 'STEUERPFLICHTIG' | 'STEUERFREI', uid: string, now: Date): Record<string, unknown> =>
    ({[`vacancyIntent.${unitID}`]: intent, updatedAt: now, updatedBy: uid});

export const buildDismissedHintUpdate = (periodID: string, hintID: string, uid: string, now: Date): Record<string, unknown> =>
    ({[`dismissedHints.${periodID}_${hintID}`]: {by: uid, at: now}, updatedAt: now, updatedBy: uid});
